import React from "react";
import dayjs, { Dayjs } from "dayjs";
import { UsimInventoryItem } from "../../../../api/usim/inventory/inventoryItem";
import { Button, Input, Select, DatePicker } from "antd";
import type { ColumnsType } from "antd/es/table";
import { DeleteOutlined } from "@ant-design/icons";
import { UsimSkuDropdown } from "../../shared/usimSkuDropdown";
import { USimStatus, USimStatusOptions, toUSimStatus } from "../../shared/usimUtils";

export interface TableRowData {
  key: string;
  iccid: string;
  mobileNumber: string;
  productType: string;
  productPlan: string;
  status: string;
  activationDate: Dayjs | null;
  inventoryId: string;
  puk: string;
  hasActivation: boolean;
}

export interface GetColumnsProps {
  handleDeleteRow: (key: string) => void;
  handleTableDataChange: (key: string, field: keyof TableRowData, value: any) => void;
  setTempIccid: (val: string) => void;
  setTempRowKey: (val: string) => void;
  handleKeyPress: (e: React.KeyboardEvent<HTMLInputElement>, key: string, field: keyof TableRowData) => void;
  tempIccid: string;
  currentIccid: string;
  setCurrentIccid: (val: string) => void;
  tempMobileNumber: string;
  currentMobileNumber: string;
  setCurrentMobileNumber: (val: string) => void;
  setTempMobileNumber: (val: string) => void;
  productTypesOptions: { id: string | undefined, label: string, value: string, iccid: boolean, hasActivation: boolean }[];
  setSelectedProductType: (val: string) => void;
  handleSelectKeyPress: (e: React.KeyboardEvent, key: string, field: keyof TableRowData) => void;
  productPlansOptions: { label: string, value: string, type?: string }[];
  productPlansForTypeFetching: boolean;
}

// status definition
// 1. if sku / type has arrival date required then "Pending-Activation"
// 2. If sku / type has missing plan or have api recharge then "Pending-Recharge"
// 3. if Inventory already have the sku then it's completed. 
export function getInventoryStatus(productTypesOptions: { id: string | undefined, label:string, value: string, iccid:boolean, hasActivation: boolean }[], inventoryItem: UsimInventoryItem) {
  console.log("inventoryItem", inventoryItem)
  const productType = productTypesOptions?.find(x => x.id === inventoryItem.typeId);
  console.log("product Type", productType)
  if (productType?.hasActivation) {
    return USimStatus.PENDING_PROCESSING;
  }
  else if (!inventoryItem.planName || inventoryItem.planName === "" || inventoryItem.planName === "-" || inventoryItem.planName?.startsWith("joy")) {
    return USimStatus.PENDING_PROCESSING;
  }
  else if (inventoryItem.planName.length > 1) {
    return USimStatus.COMPLETE;
  }
  else {
    return USimStatus.PENDING_PROCESSING;
  }
}

export const generateOrderNumber = () => {
  const today = dayjs().format('YYYYMMDD');
  const randomChars = Math.random().toString(36).substring(2, 8).toUpperCase();
  return `6${today}${randomChars}`;
};

export const getColumns = ({
  handleDeleteRow,
  handleTableDataChange,
  setTempIccid,
  setTempRowKey,
  handleKeyPress,
  tempIccid,
  currentIccid,
  setCurrentIccid,
  tempMobileNumber,
  currentMobileNumber,
  setCurrentMobileNumber,
  setTempMobileNumber,
  productTypesOptions,
  setSelectedProductType,
  handleSelectKeyPress,
  productPlansOptions,
  productPlansForTypeFetching
}: GetColumnsProps): ColumnsType<TableRowData> => {
  return [
    {
      title: " ",
      key: "action",
      width: 60,
      render: (_, record) => (
        <Button danger onClick={() => handleDeleteRow(record.key)}>
          <DeleteOutlined />
        </Button>
      ),
    },
    {
      title: "ICCID",
      dataIndex: "iccid",
      key: "iccid",
      width: 250, // Larger width for ICCID
      render: (_, record) => (
        <Input
          value={record.iccid}
          onChange={(e) => {
            const value = e.target.value.trim();
            handleTableDataChange(record.key, "iccid", value);
            setTempIccid(value);
            setTempRowKey(record.key);
          }}
          data-row-key={record.key}
          data-field="iccid"
          onKeyDown={(e) => handleKeyPress(e, record.key, "iccid")}
          onBlur={() => {
            if (tempIccid !== currentIccid) {
              handleTableDataChange(record.key, "mobileNumber", "");
              handleTableDataChange(record.key, "productType", "");
              handleTableDataChange(record.key, "status", USimStatus.PENDING_PROCESSING);
              console.log("old sku", record.productPlan)
              if (tempIccid) {
                setCurrentIccid(tempIccid);
              } else {
                setCurrentIccid("");
              }
            }
          }}
        />
      ),
    },
    {
      title: "Mobile Number",
      dataIndex: "mobileNumber",
      key: "mobileNumber",
      width: 150, // Smaller width for mobile number
      render: (_, record) => (
        <Input
          value={record.mobileNumber}
          onChange={(e) => {
            const value = e.target.value.trim();
            handleTableDataChange(record.key, "mobileNumber", value);
            setTempMobileNumber(value);
            setTempRowKey(record.key);
          }}
          onKeyDown={(e) => handleKeyPress(e, record.key, "mobileNumber")}
          onBlur={() => {
            if (tempMobileNumber !== currentMobileNumber) {
              // Clear related fields when mobile number changes
              handleTableDataChange(record.key, "iccid", "");
              handleTableDataChange(record.key, "productType", "");
              handleTableDataChange(record.key, "status", USimStatus.PENDING_PROCESSING);
              // Don't clear activationDate as it should be preserved from parent

              if (tempMobileNumber) {
                setCurrentMobileNumber(tempMobileNumber);
              } else {
                setCurrentMobileNumber("");
              }
            }
          }}
          placeholder="Enter mobile number"
          data-row-key={record.key}
          data-field="mobileNumber"
        />
      ),
    },
    {
      title: "Product Type",
      dataIndex: "productType",
      key: "productType",
      width: 200, // Larger width for product type
      render: (_, record) => (
        <Select
          value={record.productType}
          onChange={(value) => {
            handleTableDataChange(record.key, "productType", value);
            let data = productTypesOptions.find(item => item.value === value);
            if (data && (data.iccid == false || data.iccid.toString().toLowerCase() == "false")) {
              //set status as complete
              handleTableDataChange(record.key, "status", USimStatus.COMPLETE);
            }
            // set isActivation as true if product type has hasActivation
            const hasActivation = data?.hasActivation ?? false;
            handleTableDataChange(record.key, "hasActivation", hasActivation);
            console.log("product type", value, data);
            setSelectedProductType(value);
          }}
          onKeyDown={(e) => handleSelectKeyPress(e as any, record.key, "productType")}
          options={productTypesOptions}
          placeholder="Select product type"
          style={{ width: "100%" }}
          data-row-key={record.key}
          data-field="productType"
          showSearch
          filterOption={(input, option) =>
            (option?.label ?? "").toLowerCase().includes(input.toLowerCase())
          }
        />
      ),
    },
    {
      title: "Product SKU",
      dataIndex: "productPlan",
      key: "productPlan",
      width: 220, // Larger width for product plan
      render: (_, record) => (
        <UsimSkuDropdown
          value={record.productPlan}
          placeholder="Select product SKU"
          style={{ width: "100%" }}
          onChange={(value) =>
            handleTableDataChange(record.key, "productPlan", value as string)
          }
          typeName={record.productType}
        />
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 140, // Smaller width for status
      render: (_, record) => (
        <Select
          value={toUSimStatus(record.status)}
          onChange={(value) =>
            handleTableDataChange(record.key, "status", value)
          }
          onKeyDown={(e) => handleSelectKeyPress(e as any, record.key, "status")}
          placeholder="Select status"
          style={{ width: "100%" }}
          data-row-key={record.key}
          data-field="status"
          options={USimStatusOptions}
        />
      ),
    },
    {
      title: "Activation Date",
      dataIndex: "activationDate",
      key: "activationDate",
      width: 140, // Smaller width for status
      render: (_, record) => (
        <DatePicker
          value={record.activationDate ? dayjs(record.activationDate) : null}
          onChange={(date) => {
            const dateStr = date ? date.format('DD-MMM-YYYY') : '';
            handleTableDataChange(record.key, "activationDate", dateStr as any);
            if (dateStr) {
              handleTableDataChange(record.key, "status", USimStatus.PENDING_PROCESSING);
            }
          }}
          style={{ width: "100%" }}
          placeholder="Select activation date"
          data-row-key={record.key}
          data-field="activationDate"
        />
      ),
    },
    {
      title: "PUK/PIN",
      dataIndex: "puk",
      key: "puk",
      width: 100, // Smaller width for mobile number
      render: (_, record) => (
        <Input
          value={record.puk}
          onChange={(e) => {
            const value = e.target.value.trim();
            handleTableDataChange(record.key, "puk", value);
            setTempMobileNumber(value);
            setTempRowKey(record.key);
          }}
          onKeyDown={(e) => handleKeyPress(e, record.key, "puk")}
          placeholder="Enter PUK/PIN"
          data-row-key={record.key}
          data-field="puk"
        />
      ),
    },
  ];
};